import {ChevronRightIcon, GearIcon} from '@primer/octicons-react'
import {
  ActionList,
  ActionMenu,
  AnchoredOverlay,
  Button,
  IconButton,
  SegmentedControl,
  VisuallyHidden,
} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import {DEFAULT_INTENT_PRELOAD_DELAY} from '@github-ui/react-router'
import type React from 'react'
import {type ReactNode, useEffect, useId, useRef, useState} from 'react'

import {findField, type FieldOption} from './field-option'
import styles from './ViewOptionsMenu.module.css'

function DirtyVisual({showDirtyIndicator = false, children}: {showDirtyIndicator?: boolean; children: ReactNode}) {
  if (!showDirtyIndicator) return children
  return (
    <span className={styles.dirtyVisual}>
      {children}
      <span className={styles.dirtyIndicator} aria-hidden="true" data-testid="view-options-dirty-indicator" />
    </span>
  )
}

function DirtyGearIcon() {
  return (
    <DirtyVisual showDirtyIndicator>
      <GearIcon />
    </DirtyVisual>
  )
}

const INTENT_GROUP_ATTRIBUTE = 'data-intent-group'

/**
 * Returns props for one option that call `onIntent` once the pointer or keyboard focus has
 * stayed on it for `DEFAULT_INTENT_PRELOAD_DELAY`, the delay intent-preloading links use. Each
 * option calls it once until the pointer or focus leaves, and `cancel`, which a pick calls,
 * stops one still waiting.
 *
 * Pointer intent starts on movement, so a menu opening under a resting pointer starts nothing.
 * Focus counts only when it moves from another option of the same control: focus a menu puts
 * on its first option when it opens is not a choice.
 */
function useOptionIntent<TValue>(onIntent: ((value: TValue) => void) | undefined) {
  const groupId = useId()
  const pendingRef = useRef<{value: TValue; timer: ReturnType<typeof setTimeout> | null} | null>(null)
  const onIntentRef = useRef(onIntent)
  useEffect(() => {
    onIntentRef.current = onIntent
  })

  const cancel = () => {
    if (pendingRef.current?.timer) clearTimeout(pendingRef.current.timer)
    pendingRef.current = null
  }
  useEffect(
    () => () => {
      if (pendingRef.current?.timer) clearTimeout(pendingRef.current.timer)
    },
    [],
  )

  /** Props for the option with `value`. Every option carries the group, and only `enabled` ones listen. */
  const propsFor = (value: TValue, enabled: boolean) => {
    const group = {[INTENT_GROUP_ATTRIBUTE]: groupId}
    if (!onIntent || !enabled) return group
    const start = () => {
      if (pendingRef.current?.value === value) return
      cancel()
      const pending: {value: TValue; timer: ReturnType<typeof setTimeout> | null} = {value, timer: null}
      pending.timer = setTimeout(() => {
        pending.timer = null
        onIntentRef.current?.(value)
      }, DEFAULT_INTENT_PRELOAD_DELAY)
      pendingRef.current = pending
    }
    const leave = () => {
      if (pendingRef.current?.value === value) cancel()
    }
    return {
      ...group,
      onPointerMove: start,
      onPointerLeave: leave,
      onFocus: (event: React.FocusEvent) => {
        const from =
          event.relatedTarget instanceof Element ? event.relatedTarget.closest(`[${INTENT_GROUP_ATTRIBUTE}]`) : null
        if (from?.getAttribute(INTENT_GROUP_ATTRIBUTE) === groupId) start()
      },
      onBlur: leave,
    }
  }

  return {propsFor, cancel}
}

export interface ViewOptionsMenuProps {
  /** Accessible name for the gear trigger button (e.g. "View options"). */
  triggerLabel: string
  /** Dialog heading — visually hidden but announced to screen readers and used as the dialog's accessible name. */
  title: string
  /**
   * The consumer-composed dialog content: an ordered set of `<ViewOptionsMenu.Layout>` and
   * `<ViewOptionsMenu.Group>` sections.
   */
  children: ReactNode
  /**
   * Commits the host-owned draft state. The host wires the composed sections to a draft and
   * propagates that draft to its applied state, URL, or providers only from this callback.
   * Omit it when the host applies each change as it is made, which also drops the Apply and
   * Cancel buttons.
   */
  onApply?: () => void
  /** Discards the host-owned draft state. Called for Cancel and every other dismissal path. */
  onCancel?: () => void
  /** Disables Apply when the draft already matches the applied state. */
  isDirty?: boolean
  /** Applied settings differ from the saved view, independently of the dialog draft. */
  showDirtyIndicator?: boolean
  /**
   * Handler that is called when Discard is picked, which also closes the dialog. The
   * button shows once `showDirtyIndicator` is set and stays until the dialog closes, inactive
   * while `showDirtyIndicator` is off.
   */
  onDiscard?: () => void
  /** Prevents opening the dialog while its settings are being saved. */
  disabled?: boolean
  /** Fires with the next open state whenever the dialog opens or closes. */
  onOpenChange?: (open: boolean) => void
}

/**
 * A gear-icon trigger that opens an anchored dialog of view settings. The panel uses
 * `role="dialog"` semantics — controls are reached with `Tab`, not the arrow-key roving
 * focus of a menu. Changes are made against state owned by the host and committed through
 * the Apply button, or applied as they are made when the host passes no `onApply`. With
 * `onDiscard`, unsaved applied settings can be dropped from the dialog footer. The shell
 * does not inspect its children: a surface makes the structure
 * explicit by composing `<ViewOptionsMenu.Layout>` and `<ViewOptionsMenu.Group>` sections.
 *
 * The overlay is mounted only while open (and the gear is an external `anchorRef` rather than
 * `AnchoredOverlay`'s `renderAnchor`, since a `renderAnchor` anchor must stay mounted). Mounting
 * fresh on each open makes every open a "first open", which is what reliably moves focus into the
 * dialog — `AnchoredOverlay`'s focus trap only seizes focus on first mount, so a persistently
 * mounted overlay leaves focus on the trigger on the second and later opens.
 *
 * The overlay is intentionally non-modal (no `aria-modal`): the section `SingleSelect`s open as
 * flyout menus that need to escape this panel's clipping bounds, so they portal to the global
 * root and render as DOM siblings. It still traps keyboard focus while open — that is
 * `AnchoredOverlay`'s own behavior, independent of `aria-modal`.
 *
 * CSS anchor positioning is disabled (`cssAnchorPositioningSettings`) because with it enabled
 * `AnchoredOverlay` leaves `position` undefined, which never engages its focus trap — focus
 * would stay on the gear on open. Falling back to JS positioning keeps focus entry reliable in
 * production, where the `primer_react_css_anchor_positioning` flag is on.
 */
export function ViewOptionsMenu({
  triggerLabel,
  title,
  children,
  onApply,
  onCancel,
  isDirty = false,
  showDirtyIndicator = false,
  onDiscard,
  disabled = false,
  onOpenChange,
}: ViewOptionsMenuProps) {
  const [open, setOpenState] = useState(false)
  // Removing a focused Discard button drops focus to the body, so it stays until close.
  const [discardShown, setDiscardShown] = useState(false)
  if (open && showDirtyIndicator && !discardShown) setDiscardShown(true)
  if (!open && discardShown) setDiscardShown(false)
  const showDiscard = onDiscard !== undefined && discardShown
  const titleId = useId()
  const anchorRef = useRef<HTMLButtonElement>(null)

  const setOpen = (next: boolean) => {
    if (open !== next) onOpenChange?.(next)
    setOpenState(next)
  }

  const dismiss = () => {
    onCancel?.()
    setOpen(false)
  }

  const apply = () => {
    if (!isDirty) return
    onApply?.()
    setOpen(false)
  }

  const discard = () => {
    if (!showDirtyIndicator) return
    onDiscard?.()
    dismiss()
  }

  return (
    <>
      <IconButton
        ref={anchorRef}
        icon={showDirtyIndicator ? DirtyGearIcon : GearIcon}
        aria-label={triggerLabel}
        description={showDirtyIndicator ? 'View options have unsaved changes' : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (disabled) return
          if (open) dismiss()
          else setOpen(true)
        }}
      />
      {open && (
        <AnchoredOverlay
          open
          anchorRef={anchorRef}
          renderAnchor={null}
          onClose={dismiss}
          align="end"
          width="medium"
          variant={{regular: 'anchored', narrow: 'fullscreen'}}
          className={styles.overlay}
          displayCloseButton
          closeButtonProps={{'aria-label': 'Close view options'}}
          focusZoneSettings={{disabled: true}}
          cssAnchorPositioningSettings={{disable: true}}
          overlayProps={{role: 'dialog', 'aria-labelledby': titleId}}
        >
          <VisuallyHidden>
            <h2 id={titleId}>{title}</h2>
          </VisuallyHidden>
          <div className={styles.content}>{children}</div>
          {(onApply || showDiscard) && (
            <div className={styles.footer}>
              {showDiscard && (
                <Button
                  onClick={discard}
                  inactive={!showDirtyIndicator}
                  aria-disabled={!showDirtyIndicator || undefined}
                >
                  Discard
                </Button>
              )}
              {onApply && (
                <>
                  <Button variant="invisible" onClick={dismiss}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={apply} inactive={!isDirty} aria-disabled={!isDirty || undefined}>
                    Apply
                  </Button>
                </>
              )}
            </div>
          )}
        </AnchoredOverlay>
      )}
    </>
  )
}

interface SectionProps {
  /** Visible section heading, rendered as an `h3` and used to name the section's control. */
  label: string
  /** The id assigned to the heading, for associating the section's control. */
  labelId: string
  /** Optional trailing content in the header, e.g. a status badge. */
  trailing?: ReactNode
  children: ReactNode
}

/**
 * A labelled dialog section: a consistently styled `h3` heading and a body, drawn with a
 * divider between adjacent sections. It is a plain container, not a `region` landmark — the
 * dialog's heading hierarchy carries the structure, and each control names itself from the
 * heading, so an extra landmark would only add navigation noise.
 */
function Section({label, labelId, trailing, children}: SectionProps) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 id={labelId} className={styles.sectionLabel}>
          {label}
        </h3>
        {trailing}
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </div>
  )
}

export interface LayoutOption<TValue extends string = string> {
  value: TValue
  label: string
  icon?: React.ComponentType<{size?: number}>
}

export interface ViewOptionsLayoutProps<TValue extends string = string> {
  /** Section heading. */
  label: string
  /** Optional trailing content in the header, e.g. a status badge. */
  trailing?: ReactNode
  /** The selectable layouts, in display order. */
  options: ReadonlyArray<LayoutOption<TValue>>
  /** The currently selected layout. */
  value: TValue
  /** Applied layout with unsaved changes; it may differ from the selected draft. */
  dirtyValue?: TValue
  onSelect: (value: TValue) => void
  /** Handler that is called when the pointer or focus stays on a layout long enough to suggest it is about to be picked. */
  onIntent?: (value: TValue) => void
}

/**
 * A labelled segmented control for switching layouts. The component is agnostic of what the
 * layouts are — a surface supplies its own set (list/board, table/board/roadmap, …) and maps
 * the reported value onto its own state.
 */
function Layout<TValue extends string = string>({
  label,
  trailing,
  options,
  value,
  dirtyValue,
  onSelect,
  onIntent,
}: ViewOptionsLayoutProps<TValue>) {
  const labelId = useId()
  const dirtyDescriptionId = useId()
  const intent = useOptionIntent(onIntent)

  return (
    <Section label={label} labelId={labelId} trailing={trailing}>
      {dirtyValue !== undefined && (
        <span id={dirtyDescriptionId} hidden>
          Unsaved layout changes
        </span>
      )}
      <SegmentedControl
        aria-labelledby={labelId}
        size="small"
        fullWidth
        onChange={index => {
          intent.cancel()
          const option = options[index]
          if (option) onSelect(option.value)
        }}
      >
        {options.map(option => {
          const Icon = option.icon
          return (
            <SegmentedControl.Button
              key={option.value}
              {...intent.propsFor(option.value, option.value !== value)}
              selected={option.value === value}
              aria-describedby={option.value === dirtyValue ? dirtyDescriptionId : undefined}
              leadingVisual={
                Icon
                  ? () => (
                      <DirtyVisual showDirtyIndicator={option.value === dirtyValue}>
                        <Icon />
                      </DirtyVisual>
                    )
                  : undefined
              }
            >
              {option.label}
            </SegmentedControl.Button>
          )
        })}
      </SegmentedControl>
    </Section>
  )
}

export interface ViewOptionsGroupProps {
  /** Visible group heading, also the accessible name of the list. */
  label: string
  /** Loading or error feedback, outside the list of controls. */
  status?: ReactNode
  /** One or more `<ViewOptionsMenu.SingleSelect>` rows. */
  children: ReactNode
}

/**
 * A labelled section wrapping single-select rows. Must contain
 * `<ViewOptionsMenu.SingleSelect>` rows.
 */
function Group({label, status, children}: ViewOptionsGroupProps) {
  const labelId = useId()

  return (
    <Section label={label} labelId={labelId}>
      {status}
      {!!children && (
        <ActionList aria-labelledby={labelId} className={styles.list}>
          {children}
        </ActionList>
      )}
    </Section>
  )
}

export interface ViewOptionsSingleSelectProps {
  /** The row's leading icon. */
  icon: React.ComponentType<{size?: number}>
  /** The row's visible label, e.g. "Group by". Also names the submenu. */
  label: string
  /** Shown as the current value, and as the submenu prompt, when nothing is selected. */
  placeholder: string
  /** The selectable fields, in display order. */
  fields: readonly FieldOption[]
  /** Identifier of the selected field, or `null` when nothing is chosen. */
  value: string | null
  onSelect: (identifier: string) => void
  onClear?: () => void
  /**
   * Handler that is called when the pointer or focus stays on an option long enough to suggest
   * it is about to be picked. Called with `null` for the `placeholder` option that clears.
   */
  onIntent?: (identifier: string | null) => void
  disabled?: boolean
  showDirtyIndicator?: boolean
}

/**
 * A single-select button that opens a menu of fields — the shape "Group by", "Swimlane by",
 * and "Sort by" all share. The button shows the current selection beside a chevron, and the
 * menu marks the selected field. When `value` is `null` the button shows `placeholder` and
 * the menu marks nothing, prompting a choice rather than inventing a default. Draws nothing
 * when handed no fields. Must be rendered inside a `<ViewOptionsMenu.Group>`.
 */
function SingleSelect({
  icon: Icon,
  label,
  placeholder,
  fields,
  value,
  onSelect,
  onClear,
  onIntent,
  disabled,
  showDirtyIndicator = false,
}: ViewOptionsSingleSelectProps) {
  const dirtyDescriptionId = useId()
  const intent = useOptionIntent(onIntent)
  if (fields.length === 0) return null

  const selected = findField(fields, value)
  const currentLabel = selected?.label ?? placeholder

  const trigger = (
    <ActionList.Item disabled={disabled} aria-describedby={showDirtyIndicator ? dirtyDescriptionId : undefined}>
      <ActionList.LeadingVisual>
        <DirtyVisual showDirtyIndicator={showDirtyIndicator}>
          <Icon />
        </DirtyVisual>
      </ActionList.LeadingVisual>
      {label}
      {showDirtyIndicator && (
        <span id={dirtyDescriptionId} hidden>
          Unsaved changes
        </span>
      )}
      <ActionList.TrailingVisual>
        <span className={styles.trailingValue}>
          {currentLabel}
          <ChevronRightIcon />
        </span>
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
  if (disabled) return trigger

  return (
    // Disable CSS anchoring so the flyout does not overlap the parent view options dialog.
    <FeatureFlags flags={{primer_react_css_anchor_positioning: false}}>
      <ActionMenu onOpenChange={open => !open && intent.cancel()}>
        <ActionMenu.Anchor>{trigger}</ActionMenu.Anchor>
        <ActionMenu.Overlay width="medium" side="outside-left" align="start">
          <ActionList selectionVariant="single" aria-label={label}>
            {onClear && (
              <ActionList.Item
                {...intent.propsFor(null, value !== null)}
                selected={value === null}
                onSelect={() => {
                  intent.cancel()
                  onClear()
                }}
              >
                {placeholder}
              </ActionList.Item>
            )}
            {fields.map(field => {
              const FieldIcon = field.icon
              const isSelected = field.identifier === value
              return (
                <ActionList.Item
                  key={field.identifier}
                  {...intent.propsFor(field.identifier, !isSelected && !field.disabled)}
                  selected={isSelected}
                  disabled={field.disabled}
                  onSelect={() => {
                    intent.cancel()
                    onSelect(field.identifier)
                  }}
                >
                  <ActionList.LeadingVisual>
                    <FieldIcon />
                  </ActionList.LeadingVisual>
                  {field.label}
                </ActionList.Item>
              )
            })}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </FeatureFlags>
  )
}

export interface ViewOptionsMultiSelectProps {
  /** The row's leading icon. */
  icon: React.ComponentType<{size?: number}>
  /** The row's visible label, e.g. "Fields". Also names the submenu. */
  label: string
  /** Shown as the current value when nothing is selected. */
  placeholder: string
  /** The selectable fields, in display order. */
  fields: readonly FieldOption[]
  /** Identifiers of the fields currently on. */
  selectedIds: ReadonlySet<string>
  onToggle: (identifier: string, next: boolean) => void
  disabled?: boolean
  showDirtyIndicator?: boolean
}

/**
 * A multi-select button that opens a menu of fields, mirroring `SingleSelect`'s shape. The
 * button shows a joined summary of the currently selected labels (or `placeholder` when
 * nothing is chosen) beside a chevron. Selecting an option toggles it and — matching memex's
 * "Fields" submenu — calls `event.preventDefault()` so the parent menu stays open, letting
 * the user flip several fields in one pass. Draws nothing when handed no fields. Must be
 * rendered inside a `<ViewOptionsMenu.Group>`.
 */
function MultiSelect({
  icon: Icon,
  label,
  placeholder,
  fields,
  selectedIds,
  onToggle,
  disabled,
  showDirtyIndicator = false,
}: ViewOptionsMultiSelectProps) {
  const dirtyDescriptionId = useId()
  if (fields.length === 0) return null

  const selectedCount = fields.reduce((count, field) => (selectedIds.has(field.identifier) ? count + 1 : count), 0)
  const currentLabel = selectedCount === 0 ? placeholder : `${selectedCount} selected`

  const trigger = (
    <ActionList.Item disabled={disabled} aria-describedby={showDirtyIndicator ? dirtyDescriptionId : undefined}>
      <ActionList.LeadingVisual>
        <DirtyVisual showDirtyIndicator={showDirtyIndicator}>
          <Icon />
        </DirtyVisual>
      </ActionList.LeadingVisual>
      {label}
      {showDirtyIndicator && (
        <span id={dirtyDescriptionId} hidden>
          Unsaved changes
        </span>
      )}
      <ActionList.TrailingVisual>
        <span className={styles.trailingValue}>
          {currentLabel}
          <ChevronRightIcon />
        </span>
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
  if (disabled) return trigger

  return (
    // Disable CSS anchoring so the flyout does not overlap the parent view options dialog.
    <FeatureFlags flags={{primer_react_css_anchor_positioning: false}}>
      <ActionMenu>
        <ActionMenu.Anchor>{trigger}</ActionMenu.Anchor>
        <ActionMenu.Overlay width="medium" side="outside-left" align="start">
          <ActionList selectionVariant="multiple" aria-label={label}>
            {fields.map(field => {
              const FieldIcon = field.icon
              const selected = selectedIds.has(field.identifier)
              return (
                <ActionList.Item
                  key={field.identifier}
                  selected={selected}
                  disabled={field.disabled}
                  onSelect={event => {
                    event.preventDefault()
                    onToggle(field.identifier, !selected)
                  }}
                >
                  <ActionList.LeadingVisual>
                    <FieldIcon />
                  </ActionList.LeadingVisual>
                  {field.label}
                </ActionList.Item>
              )
            })}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </FeatureFlags>
  )
}

ViewOptionsMenu.Layout = Layout
ViewOptionsMenu.Group = Group
ViewOptionsMenu.SingleSelect = SingleSelect
ViewOptionsMenu.MultiSelect = MultiSelect
